import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useUser } from "@/requests/hooks/user";
import SplashScreen from "@/components/SplashScreen";
import { useFlags } from "flagsmith/react";
import { IFeatureFlagKeys } from "@/utils/featureFlags";
import { useEffect } from "react";
import flagsmith from "flagsmith";
import { logoutSuccessCallback } from "@/requests/callbacks/user";
import { useQueryClient } from "@tanstack/react-query";
import useHasInitialLoadCompleted from "@/utils/useHasInitialLoadCompleted";

interface IProps {
  children: React.ReactNode;
}

const ProtectedRoute = ({ children }: IProps) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    data: user,
    isLoading: isUserLoading,
    isRefetching: isUserRefetching,
    isSuccess: isUserSuccess,
    isError: isUserError,
  } = useUser();

  const flags = useFlags<IFeatureFlagKeys>(["verify-email-code-enabled"]);
  const hasInitialLoadCompleted = useHasInitialLoadCompleted();

  useEffect(() => {
    if (isUserError) {
      logoutSuccessCallback(queryClient, navigate, true, true);
    }
  }, [isUserError]);

  useEffect(() => {
    if (isUserSuccess && flagsmith.identity !== user?.email) {
      flagsmith.identify(user.email);
    }
  }, [isUserSuccess]);

  if (isUserLoading && !isUserRefetching) return <SplashScreen />;

  if (
    flags?.["verify-email-code-enabled"]?.enabled &&
    typeof user?.emailVerified === "boolean" &&
    !user?.emailVerified &&
    !pathname.includes("/auth/email-verify") &&
    !pathname.includes("company-selector")
  )
    return <Navigate to="/auth/email-verify" />;

  if (
    !hasInitialLoadCompleted &&
    user?.activeOnboardingFlow === "MARKETING" &&
    !pathname.startsWith("/auth/company-selector") &&
    !pathname.startsWith("/onboarding/marketing/")
  ) {
    return <Navigate to="/onboarding/marketing/" />;
  }

  if (
    !hasInitialLoadCompleted &&
    !pathname.startsWith("/auth/company-selector") &&
    user?.activeOnboardingFlow === "COMPLY"
  ) {
    return <Navigate to="/comply/policies" />;
  }

  return children;
};

export default ProtectedRoute;
